import React, { useState } from 'react';
import { View } from 'react-native';

import Item from './components/item';
import Loading, { SmallLoading } from '@components/loading';
import Refresh from '@components/common/Refresh';

import { isPhone, calculateTableConfig } from '@helpers';
import { getIsSquareMode, padDataForGrid, getItemWidth, getDataGridColumns } from './helpers';

import { scale, fullWidth } from '@components/frame';
import { TILE_CONFIG } from '@v2/pages/common/constants/base.constants';
import DataGrid from '@components/common/DataGrid';

import BatchList from '@components/machines/components/detailsComponents/activeMachine/components/BatchList';
import { BATCH_LIST_GAP, SCREEN_PADDING_HORIZONTAL, TABLE_NAMES, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { useVisibleItemIndexes } from '@v2/pages/machines/hooks/useGetVisibleItemIndexes';
import { connect } from 'react-redux';
import { setBatchSquareSize } from '@reducers/batch';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import { useManageProductionBatches } from './hook/useManageProductionBatches';

const ProductionBatches = (props) => {
  const {
    totalSearchResult,
    searchResult,
    isError,
    loaded,
    C,
    isSmallMode,
    isLineMode,
    fetchData,
    generateImageURL,
    recentlyViewed,
    setRecentlyViewed,
    isTableMode,
    isInfoTilesMode,
    setSelectedNCFile,
    facets,
    withoutLoader,
    withoutHorizontalPaddings,
    containerWidth,
    marginTop,
    bg,
    batchListContainerHeight,
    batchTileHeight,
    batchListYPosition,
    batchSquareSize,
    noSearchScreen,
    backgroundColorForTile,
    attachMode,
    withoutImage,
  } = props;

  const [selectedItem, setSelectedItem] = useState(null);

  const { open } = useManageProductionBatches({
    recentlyViewed,
    setRecentlyViewed,
    setSelectedNCFile,
    setSelectedItem,
  });

  const isSquareMode = getIsSquareMode({ isInfoTilesMode, isSmallMode, isLineMode, isTableMode });

  const batchListWidth = containerWidth ?? fullWidth;

  const itemsPerRow = isSquareMode ? Math.floor(batchListWidth / batchSquareSize.width) : 1;

  const { firstVisibleIndex, lastVisibleIndex } = useVisibleItemIndexes({
    containerHeight: batchListContainerHeight,
    itemGap: isInfoTilesMode ? BATCH_LIST_GAP : isSquareMode ? 44 : 0,
    itemHeight: isInfoTilesMode ? batchTileHeight : isSquareMode ? batchSquareSize?.height : 0,
    scrollTop: batchListYPosition,
    totalItems: searchResult.length,
    itemsPerRow,
  });

  const dataGridColumns = getDataGridColumns({ open, attachMode });

  const config = {
    width: batchListWidth,
    scale,
    initialBaseSize: isSmallMode ? TILE_CONFIG.BASE_WIDTH_SMALL : TILE_CONFIG.BASE_WIDTH,
    spaceBetween: isSmallMode ? TILE_CONFIG.SPACE_BETWEEN_SMALL : TILE_CONFIG.SPACE_BETWEEN_NORMAL,
  };

  const { columns, itemSize } = calculateTableConfig(config);
  const { IS_NARROW_WEB } = useDeviceSize();

  const itemWidth = getItemWidth({ batchListWidth, itemSize, isLineMode, IS_NARROW_WEB });

  const data = padDataForGrid(searchResult, columns);

  if (isTableMode) {
    return (
      <View
        testID={TEST_IDS.searchScreen.productionBatches.dataGridContainer}
        style={{ width: batchListWidth, height: '100%', marginHorizontal: 'auto', marginTop }}
      >
        <DataGrid
          {...{
            ...props,
            primaryKey: 'batchNumber',
            onRowClick: open,
            columns: dataGridColumns,
            data,
            idName: 'productionBatchId',
            tableName: props.tableName || TABLE_NAMES.productionBatch,
            bg,
          }}
        />
      </View>
    );
  }

  if (isError) {
    return <Refresh onPress={fetchData} />;
  }

  if (!loaded) {
    <View style={{ width: batchListWidth, height: '100%' }}>
      <Loading />
    </View>;
  }

  const Loader = (
    <View
      style={{
        marginTop: scale(20),
        backgroundColor: C('Background / Light / System'),
        justifyContent: 'center',
      }}
    >
      <SmallLoading />
    </View>
  );

  if (isInfoTilesMode) {
    return (
      <View
        testID={TEST_IDS.searchScreen.productionBatches.batchListContainer}
        style={[
          { flex: 1, width: batchListWidth, marginHorizontal: 'auto' },
          !withoutHorizontalPaddings && { paddingHorizontal: scale(!isPhone ? SCREEN_PADDING_HORIZONTAL : 0) },
          (!noSearchScreen || IS_NARROW_WEB) && { marginTop },
        ]}
      >
        {!searchResult?.length && !withoutLoader && Loader}
        <BatchList
          batchListWidth={batchListWidth}
          batchList={searchResult}
          withoutHorizontalPaddings={withoutHorizontalPaddings}
          firstVisibleIndex={firstVisibleIndex}
          lastVisibleIndex={lastVisibleIndex}
          bg={backgroundColorForTile}
          withoutImage={withoutImage}
        />
      </View>
    );
  }

  return (
    <View testID={TEST_IDS.searchScreen.productionBatches.itemContainer} style={{ width: batchListWidth, height: '100%' }}>
      <View>
        <View
          style={[
            {
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginTop: scale(marginTop ? marginTop : isPhone ? -9 : -5),
              justifyContent: 'space-between',
              flexDirection: 'row',
              flexWrap: 'wrap',
            },
            !withoutHorizontalPaddings && { paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL) },
            isLineMode && !marginTop && { marginTop: 0 },
          ]}
        >
          {data.map((item, index) => (
            <Item
              key={'Item' + index}
              item={item}
              {...{
                index,
                itemSize: itemWidth,
                generateImageURL,
                isSmallMode,
                isLineMode,
                recentlyViewed,
                setRecentlyViewed,
                open,
                isSelected: selectedItem === item.productionBatchId,
                firstVisibleIndex,
                lastVisibleIndex,
              }}
              isEmpty={item === 'empty'}
              noSearchScreen={noSearchScreen}
            />
          ))}
        </View>
        {!withoutLoader && totalSearchResult !== searchResult?.length && Loader}
      </View>
    </View>
  );
};

export default connect(
  ({ batch: { batchListContainerHeight, batchTileSize, batchListYPosition, batchSquareSize, batchTileHeight } }) => ({
    batchListContainerHeight,
    batchSquareSize,
    batchTileSize,
    batchListYPosition,
    batchTileHeight,
  }),
  { setBatchSquareSize }
)(ProductionBatches);
